import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    CircularProgress,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { albumService } from '../../api/albums';
import { Album } from '../../types/entities/album.entity';

interface GalleryAlbumsSectionProps {
    activeAlbumId?: string;
    onCreateAlbum: () => void;
}

export const GalleryAlbumsSection = ({ activeAlbumId, onCreateAlbum }: GalleryAlbumsSectionProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['albums'],
        queryFn: () => albumService.getAll(1),
    });

    const deleteMutation = useMutation({
        mutationFn: albumService.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['albums'] });
            queryClient.invalidateQueries({ queryKey: ['images'] });
        },
    });

    const albums = data?.data.albums ?? [];

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    const renderAlbumCard = (album: Album, isActive: boolean) => (
        <Card
            key={album.id}
            variant="outlined"
            sx={{
                borderColor: isActive ? 'primary.main' : 'divider',
                borderWidth: isActive ? 2 : 1,
            }}
        >
            <CardActionArea onClick={() => navigate(`/gallery/album/${album.id}`)}>
                {album.cover_url ? (
                    <CardMedia
                        component="img"
                        height="120"
                        image={album.cover_url}
                        alt={album.name}
                        sx={{ objectFit: 'cover' }}
                    />
                ) : (
                    <Box
                        height={120}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bgcolor="action.hover"
                    >
                        <PhotoLibraryIcon color="disabled" sx={{ fontSize: 40 }} />
                    </Box>
                )}
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Box minWidth={0}>
                            <Typography variant="subtitle2" noWrap>
                                {album.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('gallery.albumPhotoCount', { count: album.images_count })}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            color="error"
                            aria-label="delete album"
                            onClick={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                if (window.confirm(t('gallery.deleteAlbumConfirmation'))) {
                                    deleteMutation.mutate(album.id, {
                                        onSuccess: () => {
                                            if (activeAlbumId === album.id) {
                                                navigate('/gallery');
                                            }
                                        },
                                    });
                                }
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );

    return (
        <Box mb={4}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">{t('gallery.albumsTitle')}</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={onCreateAlbum}>
                    {t('gallery.createAlbum')}
                </Button>
            </Stack>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                        md: 'repeat(4, 1fr)',
                    },
                    gap: 2,
                }}
            >
                <Card
                    variant="outlined"
                    sx={{
                        borderColor: !activeAlbumId ? 'primary.main' : 'divider',
                        borderWidth: !activeAlbumId ? 2 : 1,
                    }}
                >
                    <CardActionArea onClick={() => navigate('/gallery')}>
                        <Box
                            height={120}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor="action.selected"
                        >
                            <PhotoLibraryIcon color="primary" sx={{ fontSize: 40 }} />
                        </Box>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="subtitle2">{t('gallery.allPhotos')}</Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>

                {albums.map((album) => renderAlbumCard(album, activeAlbumId === album.id))}
            </Box>
        </Box>
    );
};
